import { client } from '@client/graphql/client.ts';
import { OnlineOrderBranchSettingFragment } from '@client/graphql/online-order-branch-setting-fragment';
import { getFragmentData } from '@client/graphql/types';
import {
  OnlineOrderPaymentMethod,
  OnlinePaymentMethod,
} from '@client/graphql/types/graphql.ts';
import ApplicationErrorView from '@client/module/error/application-error-view';
import getImageLink from '@packages/core/image-source/get-image-link.ts';
import { uploadImage } from '@packages/core/uploading/upload-image.ts';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { useTranslate } from '@tolgee/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PrimaryButton from 'ui/src/button/primary-button.tsx';
import CardContent from 'ui/src/card/card-content.tsx';
import Card from 'ui/src/card/card.tsx';
import Center from 'ui/src/center';
import FormItem from 'ui/src/form/form-item.tsx';
import FormLayout from 'ui/src/form/form-layout.tsx';
import TextInput from 'ui/src/form/input/text-input.tsx';
import ToggleInput from 'ui/src/form/input/toggle-input.tsx';
import { useNotificationCenter } from 'ui/src/notification/notification-center.ts';
import Spinner from 'ui/src/spinner';
import { useFocusedBranchContext } from '../../table-group/table-group-list/component/branch-selection/focused-branch-context.ts';
import useOnlineOrderBranchSetting from './logic/use-online-order-branch-setting.ts';
import useUpdateOnlineOrderBranchSetting from './logic/use-update-online-order-branch-setting.ts';

const MAX_METHODS = 3;

type FieldRow = { key: string; value: string };

type EditableMethod = {
  id: string;
  name: string;
  fields: FieldRow[];
  qrImageOrigin: string;
  qrImageThumbnail: string;
  instructions: string;
};

function makeEmptyMethod(): EditableMethod {
  return {
    id: crypto.randomUUID(),
    name: '',
    fields: [{ key: '', value: '' }],
    qrImageOrigin: '',
    qrImageThumbnail: '',
    instructions: '',
  };
}

function fromGraphqlMethod(m: OnlinePaymentMethod): EditableMethod {
  return {
    id: m.id,
    name: m.name,
    fields:
      m.fields.length > 0
        ? m.fields.map((f) => ({ key: f.key, value: f.value }))
        : [{ key: '', value: '' }],
    qrImageOrigin: m.qrImageOrigin ?? '',
    qrImageThumbnail: m.qrImageThumbnail ?? '',
    instructions: m.instructions ?? '',
  };
}

export default function OnlineOrderOnlinePaymentSetting() {
  const navigate = useNavigate();
  const branchProvider = useFocusedBranchContext();
  const branchID = branchProvider.branch?.id ?? '';
  const { showAlert } = useNotificationCenter();
  const { t } = useTranslate();

  const { data, loading, error, refetch } = useOnlineOrderBranchSetting(branchID);
  const [updateOnlineOrderBranchSetting, { loading: updateLoading }] = useUpdateOnlineOrderBranchSetting();

  const [enabled, setEnabled] = useState(false);
  const [methods, setMethods] = useState<EditableMethod[]>([]);
  const [uploadingMethodId, setUploadingMethodId] = useState<string | null>(null);
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const settingData = getFragmentData(OnlineOrderBranchSettingFragment, data?.onlineOrderBranchConfiguration);

  useEffect(() => {
    if (!settingData) return;
    setEnabled(settingData.paymentMethods.includes(OnlineOrderPaymentMethod.ManualBankTransfer));
    setMethods(
      settingData.onlinePaymentMethods.length > 0
        ? settingData.onlinePaymentMethods.map(fromGraphqlMethod)
        : [],
    );
  }, [settingData]);

  const savedSnapshot = useMemo(() => {
    if (!settingData) return null;
    return JSON.stringify({
      enabled: settingData.paymentMethods.includes(OnlineOrderPaymentMethod.ManualBankTransfer),
      methods: settingData.onlinePaymentMethods.map(fromGraphqlMethod),
    });
  }, [settingData]);

  const currentSnapshot = useMemo(
    () => JSON.stringify({ enabled, methods }),
    [enabled, methods],
  );

  const isDirty = savedSnapshot !== null && savedSnapshot !== currentSnapshot;

  const handleAddMethod = () => {
    if (methods.length >= MAX_METHODS) return;
    setMethods((prev) => [...prev, makeEmptyMethod()]);
  };

  const handleRemoveMethod = (id: string) => {
    setMethods((prev) => prev.filter((m) => m.id !== id));
  };

  const updateMethod = (id: string, updater: (m: EditableMethod) => EditableMethod) => {
    setMethods((prev) => prev.map((m) => (m.id === id ? updater(m) : m)));
  };

  const handleAddField = (id: string) => {
    updateMethod(id, (m) => ({ ...m, fields: [...m.fields, { key: '', value: '' }] }));
  };

  const handleRemoveField = (id: string, idx: number) => {
    updateMethod(id, (m) => ({
      ...m,
      fields: m.fields.length === 1 ? m.fields : m.fields.filter((_, i) => i !== idx),
    }));
  };

  const handleFieldChange = (id: string, idx: number, key: 'key' | 'value', value: string) => {
    updateMethod(id, (m) => ({
      ...m,
      fields: m.fields.map((f, i) => (i === idx ? { ...f, [key]: value } : f)),
    }));
  };

  const handleUploadQr = async (id: string, file: File) => {
    setUploadingMethodId(id);
    try {
      const response = await uploadImage(client, file);
      const originPath = response.data?.preSignImageUpload.originPath;
      const thumbnailPath = response.data?.preSignImageUpload.thumbnailPath;
      if (originPath && thumbnailPath) {
        updateMethod(id, (m) => ({
          ...m,
          qrImageOrigin: originPath,
          qrImageThumbnail: thumbnailPath,
        }));
      }
    } catch (e) {
      showAlert({
        status: 'error',
        title: t('online-order.online-payment.upload.error.title', 'Upload failed'),
        message:
          e instanceof Error && e.message
            ? e.message
            : t('online-order.online-payment.upload.error.message', 'Could not upload QR image'),
      });
    } finally {
      setUploadingMethodId(null);
    }
  };

  const handleSave = () => {
    // Filter empty field rows; keep methods even with no fields so backend can flag them.
    const trimmedMethods = methods.map((m) => ({
      id: m.id,
      name: m.name.trim(),
      fields: m.fields
        .map((f) => ({ key: f.key.trim(), value: f.value.trim() }))
        .filter((f) => f.key.length > 0 && f.value.length > 0),
      qrImageOrigin: m.qrImageOrigin || null,
      qrImageThumbnail: m.qrImageThumbnail || null,
      instructions: m.instructions.trim() || null,
    }));

    if (enabled) {
      if (trimmedMethods.length === 0) {
        showAlert({
          status: 'error',
          title: t('online-order.online-payment.validation.title', 'Cannot save'),
          message: t(
            'online-order.online-payment.validation.empty',
            'Add at least one payment method or disable online payment.',
          ),
        });
        return;
      }
      for (const m of trimmedMethods) {
        if (!m.name) {
          showAlert({
            status: 'error',
            title: t('online-order.online-payment.validation.title', 'Cannot save'),
            message: t(
              'online-order.online-payment.validation.method-name',
              'Each payment method needs a name.',
            ),
          });
          return;
        }
        if (m.fields.length === 0) {
          showAlert({
            status: 'error',
            title: t('online-order.online-payment.validation.title', 'Cannot save'),
            message: t(
              'online-order.online-payment.validation.method-fields',
              'Method "{name}" needs at least one field with both name and value.',
              { name: m.name },
            ),
          });
          return;
        }
      }
    }

    const others =
      settingData?.paymentMethods.filter((m) => m !== OnlineOrderPaymentMethod.ManualBankTransfer) ?? [];
    const paymentMethods = enabled ? [...others, OnlineOrderPaymentMethod.ManualBankTransfer] : others;

    updateOnlineOrderBranchSetting({
      variables: {
        branchID,
        input: {
          paymentMethods,
          onlinePaymentMethods: trimmedMethods,
        },
      },
    })
      .then(() => {
        showAlert({
          status: 'success',
          title: t('online-order.online-payment.update.success.title', 'Success'),
          message: t(
            'online-order.online-payment.update.success.message',
            'Online payment settings updated',
          ),
        });
        void refetch();
      })
      .catch((err: Error) => {
        showAlert({
          status: 'error',
          title: t('online-order.online-payment.update.error.title', 'Error'),
          message: err.message,
        });
      });
  };

  if (loading) {
    return (
      <Center className="p-8">
        <Spinner />
      </Center>
    );
  }

  if (error) {
    return <ApplicationErrorView error={error} />;
  }

  if (!data?.onlineOrderBranchConfiguration) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="sticky top-0 z-20 -mx-2 md:-mx-4 px-2 md:px-4 py-3 bg-white/90 backdrop-blur border-b border-gray-200 shadow-sm">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="rounded-md p-1 hover:bg-gray-100"
            aria-label="Back"
          >
            <ChevronLeftIcon className="w-6 h-6 text-gray-700" />
          </button>
          <h1 className="text-xl font-semibold text-gray-900 flex-1 truncate">
            {t('online-order.online-payment.title', 'Online Payment Methods')}
          </h1>
          <PrimaryButton
            onClick={handleSave}
            loading={updateLoading}
            disabled={updateLoading || !isDirty}
          >
            {t('common.button.save', 'Save')}
          </PrimaryButton>
        </div>
      </div>

      <Card>
        <CardContent>
          <FormLayout>
            <FormItem title={t('online-order.online-payment.enabled', 'Enable online payment')}>
              <ToggleInput value={enabled} onChange={setEnabled} />
              <p className="my-2 text-sm text-gray-500">
                {t(
                  'online-order.online-payment.enabled.description',
                  'Customers transfer manually using one of the methods below, then upload a payment screenshot. You verify each transfer in the order list before confirming.',
                )}
              </p>
            </FormItem>

            {enabled && (
              <p className="text-sm text-gray-600">
                {t(
                  'online-order.online-payment.help',
                  'Add payment methods (bank transfer, e-wallet, card details, etc.). Each method needs a name and at least one field. Customers can copy any field with one tap.',
                )}
              </p>
            )}
          </FormLayout>
        </CardContent>
      </Card>

      {enabled && (
        <>
          <div className="space-y-4">
            {methods.map((method, methodIdx) => (
              <Card key={method.id}>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-base font-semibold text-gray-900">
                        {t('online-order.online-payment.method.title', 'Payment method {n}', {
                          n: methodIdx + 1,
                        })}
                      </h3>
                      {methods.length > 1 && (
                        <button
                          type="button"
                          className="text-sm text-red-600 hover:underline"
                          onClick={() => handleRemoveMethod(method.id)}
                        >
                          {t('online-order.online-payment.method.remove', 'Remove')}
                        </button>
                      )}
                    </div>

                    <FormItem title={t('online-order.online-payment.method.name', 'Method name')}>
                      <TextInput
                        type="text"
                        label={`method-name-${method.id}`}
                        name={`method-name-${method.id}`}
                        placeholder={t(
                          'online-order.online-payment.method.name.placeholder',
                          'e.g. Vietcombank, Visa Card, MoMo',
                        )}
                        value={method.name}
                        onChange={(e) =>
                          updateMethod(method.id, (m) => ({ ...m, name: e.target.value }))
                        }
                      />
                    </FormItem>

                    <div className="space-y-2">
                      <div className="text-sm font-medium text-gray-700">
                        {t('online-order.online-payment.method.fields', 'Fields')}
                      </div>
                      <p className="text-xs text-gray-500">
                        {t(
                          'online-order.online-payment.method.fields.help',
                          'Each row is a label/value pair shown to customers (e.g. "Account number" / "0123456789"). Add as many as you need.',
                        )}
                      </p>

                      {method.fields.map((field, idx) => (
                        <div
                          key={idx}
                          className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-start"
                        >
                          <input
                            type="text"
                            placeholder={t(
                              'online-order.online-payment.method.field.key.placeholder',
                              'Field name (e.g. Account number)',
                            )}
                            value={field.key}
                            onChange={(e) =>
                              handleFieldChange(method.id, idx, 'key', e.target.value)
                            }
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                          />
                          <input
                            type="text"
                            placeholder={t(
                              'online-order.online-payment.method.field.value.placeholder',
                              'Field value (e.g. 0123456789)',
                            )}
                            value={field.value}
                            onChange={(e) =>
                              handleFieldChange(method.id, idx, 'value', e.target.value)
                            }
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                          />
                          <button
                            type="button"
                            className="text-sm text-red-600 hover:underline px-2 py-1.5 disabled:opacity-30 disabled:hover:no-underline"
                            onClick={() => handleRemoveField(method.id, idx)}
                            disabled={method.fields.length === 1}
                          >
                            {t('online-order.online-payment.method.field.remove', 'Remove')}
                          </button>
                        </div>
                      ))}

                      <button
                        type="button"
                        className="text-sm text-indigo-600 hover:underline"
                        onClick={() => handleAddField(method.id)}
                      >
                        {t('online-order.online-payment.method.field.add', '+ Add field')}
                      </button>
                    </div>

                    <FormItem
                      title={t('online-order.online-payment.method.qr', 'QR code (optional)')}
                    >
                      <p className="text-xs text-gray-500 mb-2">
                        {t(
                          'online-order.online-payment.method.qr.help',
                          'Optional QR image for one-tap scanning in the customer\'s banking app.',
                        )}
                      </p>
                      <div className="flex items-center gap-4">
                        {method.qrImageThumbnail ? (
                          <img
                            src={getImageLink(method.qrImageThumbnail)}
                            alt="QR preview"
                            className="w-32 h-32 object-cover rounded-lg border border-gray-300"
                          />
                        ) : (
                          <div className="w-32 h-32 flex items-center justify-center rounded-lg border border-dashed border-gray-300 text-xs text-gray-400 text-center px-2">
                            {t('online-order.online-payment.method.qr.empty', 'No QR uploaded')}
                          </div>
                        )}
                        <div className="flex flex-col gap-2">
                          <input
                            ref={(el) => {
                              fileInputRefs.current[method.id] = el;
                            }}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) void handleUploadQr(method.id, file);
                              e.target.value = '';
                            }}
                          />
                          <button
                            type="button"
                            className="px-4 py-2 rounded-md bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50"
                            onClick={() => fileInputRefs.current[method.id]?.click()}
                            disabled={uploadingMethodId === method.id}
                          >
                            {uploadingMethodId === method.id
                              ? t(
                                  'online-order.online-payment.method.qr.uploading',
                                  'Uploading...',
                                )
                              : method.qrImageThumbnail
                                ? t('online-order.online-payment.method.qr.replace', 'Replace QR')
                                : t('online-order.online-payment.method.qr.upload', 'Upload QR')}
                          </button>
                          {method.qrImageThumbnail && (
                            <button
                              type="button"
                              className="px-4 py-2 rounded-md text-sm text-red-600 hover:underline"
                              onClick={() =>
                                updateMethod(method.id, (m) => ({
                                  ...m,
                                  qrImageOrigin: '',
                                  qrImageThumbnail: '',
                                }))
                              }
                            >
                              {t('online-order.online-payment.method.qr.remove', 'Remove')}
                            </button>
                          )}
                        </div>
                      </div>
                    </FormItem>

                    <FormItem
                      title={t(
                        'online-order.online-payment.method.instructions',
                        'Instructions (optional)',
                      )}
                    >
                      <textarea
                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        rows={3}
                        placeholder={t(
                          'online-order.online-payment.method.instructions.placeholder',
                          'e.g. Use your order code as the transfer note',
                        )}
                        value={method.instructions}
                        onChange={(e) =>
                          updateMethod(method.id, (m) => ({ ...m, instructions: e.target.value }))
                        }
                      />
                    </FormItem>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div>
            <button
              type="button"
              className="px-4 py-2 rounded-md bg-white border border-indigo-300 text-indigo-700 text-sm hover:bg-indigo-50 disabled:opacity-40 disabled:hover:bg-white"
              onClick={handleAddMethod}
              disabled={methods.length >= MAX_METHODS}
            >
              {methods.length >= MAX_METHODS
                ? t(
                    'online-order.online-payment.add.max',
                    'You can add up to {max} payment methods',
                    { max: MAX_METHODS },
                  )
                : t('online-order.online-payment.add', '+ Add payment method')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
